import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { X, ArrowRight, ArrowLeft, Check, Volume2, VolumeX } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AVATAR_URL = "https://static.prod-images.emergentagent.com/jobs/795be194-f17c-410a-afd4-0981af84f9b2/images/1b0561bd0f3b65f0c0becc89c2366f65043aecc0ce812b2fd9f0d1a43b7d93e7.png";

/* TutorialOverlay — Heavenly Assistant guided walk-through.
   The mascot literally FLIES from one element to the next, speech bubble
   trailing behind. Each step: spotlight cuts a hole in the dark backdrop,
   avatar lerps to the target's center, bubble explains. Autoplays via
   speech synthesis (browser native) when sound is on. Persists per-user. */

const STEPS_EN = [
  {
    id: 'welcome',
    selector: null,
    title: "Hello — I'm Heavenly!",
    body: "Welcome to Dreams Rose Hall. I'm so excited you're here. This app is your command center for the entire reconstruction — from the very first demolition photo to the day we open the doors again. Every render, every milestone, every executive on the team, every single project — it's all here, and I'll show you exactly where to find it. Ready? Let's fly through it together.",
  },
  { id: 'hero',           selector: '[data-testid="hero-section"]',                     title: 'The Hero Section',          body: "This is your property header — Dreams Rose Hall, a Grupo Tortuga asset operated under the World of Hyatt Inclusive Collection. The four numbers below count down to opening day and tally what we've already shipped." },
  { id: 'tutorial',       selector: '[data-testid="tutorial-top-btn"]',                 title: 'The Tutorial Button',       body: "Right here at the top — click this button anytime to bring me back. I'll always pick up exactly where you left off." },
  { id: 'tour',           selector: '[data-testid="virtual-tour-btn"]',                 title: 'The Virtual Tour',          body: "Press play and the map turns into a fullscreen cinematic walkthrough — every render, every video, in the order our team chose. It's the fastest way to feel the rebuild from end to end." },
  { id: 'plans',          selector: '[data-testid="technical-plans-btn"]',              title: 'Technical Plans',           body: "Every PDF and every CAD file. The dot-DWG and dot-DXF drawings render as image previews automatically, so you don't need a CAD tool just to peek at a floor plan." },
  { id: 'budgets',        selector: '[data-testid="property-budgets-btn"]',             title: 'Property Budgets',          body: "The full reconstruction budget, organized by area, with subtotals and per-line drilldown. Locked to admins and engineers — guests won't see the numbers." },
  { id: 'designs',        selector: '[data-testid="ws-designs"]',                       title: 'Designs and Visualisations',body: "A workspace for every render, every mood-board, every inspiration shot we've collected — sorted by area, ready to compare." },
  { id: 'render-center',  selector: '[data-testid="render-center-btn"]',                title: 'The Design and Render Center', body: "This is one of my favorite features. Type a prompt, pick a mode, and AI generates concept renders or short videos right here in the app. Text to image, text to video, image to image, image to video. Admin only — and yes, it's as fun as it sounds." },
  { id: 'permits',        selector: '[data-testid="property-permits-btn"]',             title: 'Property Licenses and Permits', body: "Every business license, building permit, fire certificate, and shared-drive link the property team needs to keep close. Locked to admins so nothing goes missing." },
  { id: 'insurer',        selector: '[data-testid="insurer-assets-btn"]',               title: 'Insurer-Approved Disposal', body: "Photos, documents, and shared-drive links for every asset the insurance carrier has signed off for write-off. Adjusters and the GM can reconcile in seconds." },
  { id: 'search',         selector: '[data-testid="project-search-btn"]',               title: 'Project Search',            body: "Type anything — an area, a person, a status, a kind of work — and I'll surface every matching widget as a clickable card. The fastest way to answer the question 'what is Patrick working on right now?'." },
  { id: 'workspaces',     selector: '[data-testid="ws-hr"]',                            title: 'Department Workspaces',     body: "Hierarchical hubs for HR, IT, Finance, Engineering, Brand Standards, F&B, Rooms, Procurement, Storage. Padlocked tiles need admin or engineer credentials." },
  { id: 'map',            selector: '[data-testid="map-section"]',                      title: 'The Interactive Map',       body: "Click any glowing widget on the map and you'll see photos, renders, the ROI analysis, and the full enhancement list for that area. Admins can drag widgets to reposition them." },
  { id: 'roadmap',        selector: '[data-testid="reconstruction-roadmap-section"]',   title: 'The Reconstruction Roadmap',body: "Six phases. Thirty milestones. Now until March 2027. Edit and reorder phases via Admin → Planning whenever the schedule shifts." },
  { id: 'team',           selector: '[data-testid="property-details-section"]',         title: 'People Behind the Rebuild', body: "The twelve executives leading this project — operators, engineers, ownership, project managers. Tap any card to chat or call. Edit names, photos and bios under Admin → Property → Team Members." },
  { id: 'replay',         selector: '[data-testid="replay-tutorial-btn"]',              title: "You're All Set",            body: "If you're an admin, head to slash admin slash dashboard for the full CMS. Tap the little pill at the bottom right anytime to bring me back. Welcome to Dreams Rose Hall — it's going to be an extraordinary rebuild." },
];

const STEPS_ES = [
  {
    id: 'welcome',
    selector: null,
    title: "Hola — soy Heavenly",
    body: "Bienvenido a Dreams Rose Hall. Estoy muy emocionada de tenerte aquí. Esta aplicación es tu centro de control para toda la reconstrucción — desde la primera foto de demolición hasta el día que volvamos a abrir las puertas. Cada render, cada hito, cada ejecutivo del equipo, cada proyecto — todo está aquí, y te mostraré exactamente dónde encontrarlo. ¿Listo? Volemos juntos.",
  },
  { id: 'hero',           selector: '[data-testid="hero-section"]',                     title: 'La sección principal',                  body: "Este es el encabezado de tu propiedad — Dreams Rose Hall, un activo de Grupo Tortuga operado bajo la World of Hyatt Inclusive Collection. Los cuatro números de abajo cuentan los días hasta la apertura y suman lo que ya hemos completado." },
  { id: 'tutorial',       selector: '[data-testid="tutorial-top-btn"]',                 title: 'El botón del tutorial',                 body: "Justo aquí arriba — haz clic en este botón en cualquier momento para traerme de vuelta. Siempre retomaré exactamente donde lo dejaste." },
  { id: 'tour',           selector: '[data-testid="virtual-tour-btn"]',                 title: 'El tour virtual',                       body: "Pulsa reproducir y el mapa se convierte en un recorrido cinematográfico a pantalla completa — cada render, cada vídeo, en el orden que eligió nuestro equipo. Es la forma más rápida de sentir la reconstrucción de principio a fin." },
  { id: 'plans',          selector: '[data-testid="technical-plans-btn"]',              title: 'Planos técnicos',                       body: "Todos los PDF y todos los archivos CAD. Los planos punto-DWG y punto-DXF se muestran como vistas previas automáticamente, así no necesitas una herramienta CAD para echar un vistazo." },
  { id: 'budgets',        selector: '[data-testid="property-budgets-btn"]',             title: 'Presupuestos de la propiedad',          body: "El presupuesto completo de reconstrucción, organizado por área, con subtotales y desglose por línea. Bloqueado para administradores e ingenieros — los visitantes no verán los números." },
  { id: 'designs',        selector: '[data-testid="ws-designs"]',                       title: 'Diseños y visualizaciones',             body: "Un espacio para cada render, cada mood-board, cada imagen de inspiración que hemos reunido — ordenado por área, listo para comparar." },
  { id: 'render-center',  selector: '[data-testid="render-center-btn"]',                title: 'El centro de diseño y render',          body: "Esta es una de mis funciones favoritas. Escribe un prompt, elige un modo, y la IA genera renders conceptuales o vídeos cortos justo aquí en la aplicación. Texto a imagen, texto a vídeo, imagen a imagen, imagen a vídeo. Solo administradores — y sí, es tan divertido como suena." },
  { id: 'permits',        selector: '[data-testid="property-permits-btn"]',             title: 'Licencias y permisos de la propiedad',  body: "Cada licencia comercial, permiso de construcción, certificado contra incendios y enlace a unidades compartidas que el equipo necesita tener cerca. Bloqueado para administradores para que nada se pierda." },
  { id: 'insurer',        selector: '[data-testid="insurer-assets-btn"]',               title: 'Activos aprobados por la aseguradora',  body: "Fotos, documentos y enlaces a unidades compartidas para cada activo que la aseguradora ha aprobado para baja contable. Los peritos y el GM pueden conciliar en segundos." },
  { id: 'search',         selector: '[data-testid="project-search-btn"]',               title: 'Búsqueda de proyectos',                 body: "Escribe lo que sea — un área, una persona, un estado, un tipo de trabajo — y mostraré cada widget que coincida como una tarjeta clicable. La forma más rápida de responder: ¿en qué está trabajando Patrick ahora mismo?" },
  { id: 'workspaces',     selector: '[data-testid="ws-hr"]',                            title: 'Espacios de trabajo por departamento',  body: "Centros jerárquicos para Recursos Humanos, TI, Finanzas, Ingeniería, Estándares de marca, A&B, Habitaciones, Compras, Almacén. Los mosaicos con candado requieren credenciales de administrador o ingeniero." },
  { id: 'map',            selector: '[data-testid="map-section"]',                      title: 'El mapa interactivo',                   body: "Haz clic en cualquier widget brillante del mapa y verás fotos, renders, el análisis de retorno de inversión y la lista completa de mejoras para esa área. Los administradores pueden arrastrar los widgets para reposicionarlos." },
  { id: 'roadmap',        selector: '[data-testid="reconstruction-roadmap-section"]',   title: 'La hoja de ruta de la reconstrucción',  body: "Seis fases. Treinta hitos. Desde ahora hasta marzo de 2027. Edita y reordena las fases en Admin → Planificación cuando cambie el calendario." },
  { id: 'team',           selector: '[data-testid="property-details-section"]',         title: 'Las personas detrás de la reconstrucción', body: "Los doce ejecutivos que lideran este proyecto — operadores, ingenieros, propiedad, gerentes de proyecto. Toca cualquier tarjeta para chatear o llamar. Edita nombres, fotos y biografías en Admin → Propiedad → Miembros del equipo." },
  { id: 'replay',         selector: '[data-testid="replay-tutorial-btn"]',              title: "Todo listo",                            body: "Si eres administrador, ve a barra admin barra dashboard para el CMS completo. Toca esta pequeña pastilla en la esquina inferior derecha en cualquier momento para llamarme de vuelta. Bienvenido a Dreams Rose Hall — va a ser una reconstrucción extraordinaria." },
];

const STEPS_BY_LANG = { en: STEPS_EN, es: STEPS_ES };
const STEPS = STEPS_EN; // legacy reference, overridden in component

export default function TutorialOverlay({ token, autoOpen = false, onClose }) {
  const [open, setOpen] = useState(autoOpen);
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [muted, setMuted] = useState(false);
  // Language preference — persisted to localStorage so the user only
  // picks once. Defaults to English. Spanish provided per Andres' Caribbean
  // clientele (Spanish-speaking guests + vendors).
  const [lang, setLang] = useState(() => (typeof window !== 'undefined' && localStorage.getItem('tutorial_lang')) || 'en');
  const speechRef = useRef(null);
  // iter-79: prefetch every step's narration audio as soon as the
  // tutorial opens so each slide can play *instantly* when shown
  // (no ~1-2s wait for the cloud TTS fetch). Keyed by step index.
  const audioCacheRef = useRef({});

  useEffect(() => { setOpen(autoOpen); }, [autoOpen]);

  const currentSteps = STEPS_BY_LANG[lang] || STEPS_EN;
  const step = currentSteps[stepIdx];

  const onLangChange = useCallback((next) => {
    setLang(next);
    try { localStorage.setItem('tutorial_lang', next); } catch (_) { /* noop */ }
  }, []);

  // Recompute the spotlight rect every step, every resize/scroll. Scrolls
  // the target into view first so the mascot can find it on long pages.
  const measure = useCallback(() => {
    if (!step?.selector) { setRect(null); return; }
    const el = document.querySelector(step.selector);
    if (!el) { setRect(null); return; }
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const r = el.getBoundingClientRect();
        setRect({ x: r.left, y: r.top, w: r.width, h: r.height });
      });
    });
  }, [step]);

  useEffect(() => {
    if (!open) return;
    measure();
    const onR = () => measure();
    window.addEventListener('resize', onR);
    window.addEventListener('scroll', onR, { passive: true });
    return () => {
      window.removeEventListener('resize', onR);
      window.removeEventListener('scroll', onR);
    };
  }, [open, measure]);

  // iter-79 prefetch — kick off cloud TTS for EVERY step in parallel
  // the moment the tutorial opens (or the language switches). Each
  // step's HTMLAudioElement is cached so subsequent slide renders
  // play instantly with the warm "nova" voice instead of paying the
  // ~1-2s round-trip per slide.
  useEffect(() => {
    if (!open || muted) return;
    let cancelled = false;
    audioCacheRef.current = {};
    const prefetch = async (idx) => {
      const s = currentSteps[idx];
      if (!s) return;
      const text = `${s.title}. ${s.body}`;
      try {
        const { data } = await axios.post(`${API}/auth/tutorial-narration`, { text, voice: 'nova', lang });
        if (cancelled) return;
        const src = `data:${data.mime || 'audio/mpeg'};base64,${data.audio_base64}`;
        const a = new Audio(src);
        a.preload = 'auto';
        a.volume = 0.95;
        audioCacheRef.current[idx] = a;
      } catch (_) { /* fall back to native synth at play time */ }
    };
    // Fire every step's fetch in parallel — the payload is tiny and the
    // OpenAI nova endpoint handles concurrent requests fine.
    currentSteps.forEach((_, i) => { prefetch(i); });
    return () => {
      cancelled = true;
      // Pause + release every cached audio element so an aborted run
      // doesn't leak <audio> nodes or keep the OS audio session open.
      Object.values(audioCacheRef.current).forEach((a) => {
        try { a.pause(); a.src = ''; } catch (_) { /* noop */ }
      });
      audioCacheRef.current = {};
    };
  }, [open, muted, lang, currentSteps]);

  // Per-step narration — plays IMMEDIATELY when the slide renders.
  // Priority:
  //   1. Cached cloud audio (nova voice, warm)     — instant if prefetch finished
  //   2. Browser `speechSynthesis` (local voices)  — instant fallback
  // We never block on the cloud fetch on the critical path, so even
  // the very first step starts narrating within ~50ms of appearing.
  useEffect(() => {
    if (!open || muted || !step) return;
    let cancelled = false;
    let audio = null;
    const text = `${step.title}. ${step.body}`;

    const playSynth = () => {
      try {
        if (!('speechSynthesis' in window)) return;
        window.speechSynthesis.cancel();
        const utter = new SpeechSynthesisUtterance(text);
        const voices = window.speechSynthesis.getVoices() || [];
        const preferred = voices.find((v) => /samantha|karen|aria|jenny|nova|female/i.test(`${v.name} ${v.lang}`)) || voices[0];
        if (preferred) utter.voice = preferred;
        utter.rate = 1.02;
        utter.pitch = 1.08;
        utter.volume = 0.95;
        speechRef.current = utter;
        window.speechSynthesis.speak(utter);
      } catch (_) { /* noop */ }
    };

    // 1) Cache hit → play instantly.
    const cached = audioCacheRef.current[stepIdx];
    if (cached && !cancelled) {
      audio = cached;
      try {
        audio.currentTime = 0;
        const p = audio.play();
        if (p && typeof p.catch === 'function') {
          // Autoplay can be blocked on first-mount before any user
          // gesture — fall back to synth so the user still hears
          // narration immediately.
          p.catch(() => { if (!cancelled) playSynth(); });
        }
      } catch (_) {
        playSynth();
      }
    } else {
      // 2) Cache miss (prefetch hasn't landed yet — typical for
      //    step 0): start native synth instantly. We do NOT wait
      //    for the cloud; the prefetch will land in the background
      //    and replace the cached entry so a re-render or the next
      //    step plays from cache.
      playSynth();
    }

    return () => {
      cancelled = true;
      if (audio) {
        try { audio.pause(); } catch (_) { /* noop */ }
      }
      try { window.speechSynthesis?.cancel(); } catch (_) { /* noop */ }
    };
  }, [open, muted, step, stepIdx, lang]);

  const finish = useCallback(async () => {
    setOpen(false);
    try { window.speechSynthesis?.cancel(); } catch (_) { /* noop */ }
    if (token) {
      try {
        await axios.post(`${API}/auth/tutorial-completed`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (_) { /* fail silently */ }
    }
    onClose?.();
  }, [token, onClose]);

  if (!open) return null;

  // Compute avatar landing position (just outside the rect, on the side
  // with most space) and bubble position (next to the avatar).
  const VW = window.innerWidth;
  const VH = window.innerHeight;
  const AVATAR = 96;
  const BUBBLE_W = 360;

  let avX, avY, buX, buY;
  if (rect) {
    // Choose the side with the most room (right > left > below > above)
    const room = {
      right:  VW - (rect.x + rect.w),
      left:   rect.x,
      below:  VH - (rect.y + rect.h),
      above:  rect.y,
    };
    const side = Object.keys(room).reduce((a, b) => (room[a] >= room[b] ? a : b));

    if (side === 'right') {
      avX = Math.min(VW - AVATAR - 16, rect.x + rect.w + 24);
      avY = Math.max(80, rect.y + rect.h / 2 - AVATAR / 2);
      buX = avX + AVATAR + 12;
      buY = avY - 20;
    } else if (side === 'left') {
      avX = Math.max(16, rect.x - AVATAR - 24);
      avY = Math.max(80, rect.y + rect.h / 2 - AVATAR / 2);
      buX = Math.max(16, avX - BUBBLE_W - 12);
      buY = avY - 20;
    } else if (side === 'below') {
      avX = Math.max(16, Math.min(VW - AVATAR - 16, rect.x + rect.w / 2 - AVATAR / 2));
      avY = Math.min(VH - AVATAR - 16, rect.y + rect.h + 24);
      buX = Math.max(16, Math.min(VW - BUBBLE_W - 16, avX + AVATAR + 12 - BUBBLE_W / 2));
      buY = avY + AVATAR + 8;
    } else {
      avX = Math.max(16, Math.min(VW - AVATAR - 16, rect.x + rect.w / 2 - AVATAR / 2));
      avY = Math.max(16, rect.y - AVATAR - 24);
      buX = Math.max(16, Math.min(VW - BUBBLE_W - 16, avX + AVATAR + 12 - BUBBLE_W / 2));
      buY = Math.max(16, avY - 180);
    }
    buX = Math.max(16, Math.min(VW - BUBBLE_W - 16, buX));
    buY = Math.max(16, Math.min(VH - 240, buY));
  } else {
    // Welcome step — center stage
    avX = VW / 2 - AVATAR / 2;
    avY = VH / 2 - AVATAR / 2 - 40;
    buX = VW / 2 - BUBBLE_W / 2;
    buY = VH / 2 + AVATAR / 2 - 20;
  }

  const isFirst = stepIdx === 0;
  const isLast = stepIdx === currentSteps.length - 1;

  return (
    <div data-testid="tutorial-overlay" className="fixed inset-0 z-[140]" style={{ pointerEvents: 'auto' }}>
      {/* Spotlight backdrop */}
      <svg className="absolute inset-0 w-full h-full">
        <defs>
          <mask id="tutorial-mask">
            <rect width="100%" height="100%" fill="white" />
            {rect && (
              <rect
                x={Math.max(0, rect.x - 8)}
                y={Math.max(0, rect.y - 8)}
                width={rect.w + 16}
                height={rect.h + 16}
                rx="14"
                ry="14"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(15,23,42,0.72)" mask="url(#tutorial-mask)" />
      </svg>

      {/* Glow border on spotlit element */}
      {rect && (
        <div
          className="absolute pointer-events-none animate-pulse"
          style={{
            left: rect.x - 8,
            top: rect.y - 8,
            width: rect.w + 16,
            height: rect.h + 16,
            borderRadius: 14,
            boxShadow: '0 0 0 3px #FDE68A, 0 0 30px 6px rgba(253,230,138,0.6)',
          }}
        />
      )}

      {/* Flying Heavenly avatar — animates LEFT/TOP via CSS transition.
          The mascot literally flies between elements as the user advances. */}
      <div
        data-testid="tutorial-mascot"
        className="absolute pointer-events-none"
        style={{
          left: avX,
          top: avY,
          width: AVATAR,
          height: AVATAR,
          transition: 'left 700ms cubic-bezier(0.34, 1.4, 0.64, 1), top 700ms cubic-bezier(0.34, 1.4, 0.64, 1)',
          filter: 'drop-shadow(0 8px 24px rgba(99,102,241,0.5))',
          zIndex: 142,
        }}
      >
        <div className="relative w-full h-full" style={{ animation: 'tutorialFloat 2.6s ease-in-out infinite' }}>
          <img src={AVATAR_URL} alt="Heavenly Assistant" className="w-full h-full rounded-full object-cover border-4 border-white" />
          {/* sparkle ring */}
          <span className="absolute inset-0 rounded-full" style={{ boxShadow: '0 0 0 4px rgba(253,230,138,0.5)' }} />
          {/* pointing arrow toward the spotlit element */}
          {rect && <PointerArrow avatarRect={{ x: avX, y: avY, w: AVATAR, h: AVATAR }} targetRect={rect} />}
        </div>
      </div>

      {/* Speech bubble */}
      <div
        data-testid="tutorial-bubble"
        className="absolute rounded-2xl shadow-2xl pointer-events-auto"
        style={{
          left: buX,
          top: buY,
          width: BUBBLE_W,
          backgroundColor: 'white',
          fontFamily: 'Manrope',
          transition: 'left 700ms cubic-bezier(0.34, 1.4, 0.64, 1), top 700ms cubic-bezier(0.34, 1.4, 0.64, 1)',
          zIndex: 143,
        }}
      >
        <div className="flex items-center justify-between px-4 pt-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)' }}>
              <span className="text-[12px]">✨</span>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-[0.2em] font-bold" style={{ color: '#6366F1' }}>Heavenly Assistant</p>
              <p className="text-[10px]" style={{ color: '#5C6B6B' }}>{lang === 'es' ? 'Paso' : 'Step'} {stepIdx + 1} {lang === 'es' ? 'de' : 'of'} {currentSteps.length}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {/* Language picker — switches Heavenly between English and
                Spanish. Persists to localStorage so it sticks across replays. */}
            <div className="flex items-center gap-0.5 mr-1 rounded-full p-0.5" style={{ backgroundColor: '#F1F5F9' }}>
              <button
                data-testid="tutorial-lang-en"
                onClick={() => onLangChange('en')}
                className="px-2 py-0.5 rounded-full text-[10px] font-bold transition"
                style={{
                  backgroundColor: lang === 'en' ? '#6366F1' : 'transparent',
                  color: lang === 'en' ? 'white' : '#5C6B6B',
                  fontFamily: 'Manrope',
                }}
              >EN</button>
              <button
                data-testid="tutorial-lang-es"
                onClick={() => onLangChange('es')}
                className="px-2 py-0.5 rounded-full text-[10px] font-bold transition"
                style={{
                  backgroundColor: lang === 'es' ? '#6366F1' : 'transparent',
                  color: lang === 'es' ? 'white' : '#5C6B6B',
                  fontFamily: 'Manrope',
                }}
              >ES</button>
            </div>
            <button
              data-testid="tutorial-mute"
              onClick={() => {
                setMuted((m) => !m);
                try { window.speechSynthesis?.cancel(); } catch (_) { /* noop */ }
              }}
              className="p-1.5 rounded-lg hover:bg-slate-100 transition"
              title={muted ? 'Unmute narration' : 'Mute narration'}
            >
              {muted ? <VolumeX className="w-4 h-4" style={{ color: '#5C6B6B' }} /> : <Volume2 className="w-4 h-4" style={{ color: '#5C6B6B' }} />}
            </button>
            <button data-testid="tutorial-close" onClick={finish} className="p-1.5 rounded-lg hover:bg-slate-100 transition" title="Skip the tour">
              <X className="w-4 h-4" style={{ color: '#5C6B6B' }} />
            </button>
          </div>
        </div>
        <div className="px-4 pt-2 pb-3">
          <h3 className="text-base font-bold mb-1" style={{ color: '#1A2E2E' }}>{step.title}</h3>
          <p className="text-[12px] leading-relaxed" style={{ color: '#4A5D5D' }}>{step.body}</p>
        </div>
        <div className="flex items-center gap-1 px-4 pb-2">
          {currentSteps.map((s, i) => (
            <span
              key={s.id}
              className="h-1 flex-1 rounded-full transition-colors"
              style={{ backgroundColor: i <= stepIdx ? '#6366F1' : '#E5E7EB' }}
            />
          ))}
        </div>
        <div className="px-4 pb-4 flex items-center justify-between gap-2">
          <button
            data-testid="tutorial-back"
            onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
            disabled={isFirst}
            className="inline-flex items-center gap-1 text-[11px] font-bold px-3 py-1.5 rounded-lg transition disabled:opacity-40"
            style={{ color: '#4A5D5D' }}
          >
            <ArrowLeft className="w-3 h-3" /> {lang === 'es' ? 'Atrás' : 'Back'}
          </button>
          <div className="flex items-center gap-2">
            {!isLast && (
              <button data-testid="tutorial-skip" onClick={finish} className="text-[11px] font-bold px-3 py-1.5 rounded-lg" style={{ color: '#5C6B6B' }}>{lang === 'es' ? 'Saltar' : 'Skip'}</button>
            )}
            <button
              data-testid="tutorial-next"
              onClick={() => isLast ? finish() : setStepIdx((i) => i + 1)}
              className="inline-flex items-center gap-1 text-[11px] font-bold px-4 py-2 rounded-lg text-white transition hover:-translate-y-0.5"
              style={{ background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)' }}
            >
              {isLast ? (<><Check className="w-3 h-3" /> {lang === 'es' ? 'Finalizar' : 'Finish'}</>) : (<>{lang === 'es' ? 'Siguiente' : 'Next'} <ArrowRight className="w-3 h-3" /></>)}
            </button>
          </div>
        </div>
      </div>

      {/* Floating-bob keyframes */}
      <style>{`
        @keyframes tutorialFloat {
          0%, 100% { transform: translateY(0px) rotate(-3deg); }
          50%      { transform: translateY(-10px) rotate(3deg); }
        }
      `}</style>
    </div>
  );
}

// Tiny SVG arrow that emerges from the avatar and points toward the
// spotlit element. Auto-rotates based on relative position.
function PointerArrow({ avatarRect, targetRect }) {
  const ax = avatarRect.x + avatarRect.w / 2;
  const ay = avatarRect.y + avatarRect.h / 2;
  const tx = targetRect.x + targetRect.w / 2;
  const ty = targetRect.y + targetRect.h / 2;
  const angle = Math.atan2(ty - ay, tx - ax) * (180 / Math.PI);
  return (
    <span
      className="absolute"
      style={{
        left: '50%',
        top: '50%',
        width: 38,
        height: 38,
        transform: `translate(-50%, -50%) rotate(${angle}deg) translateX(56px)`,
        pointerEvents: 'none',
      }}
    >
      <svg viewBox="0 0 24 24" width="38" height="38" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.25))' }}>
        <path d="M2 12 L18 12 M14 7 L19 12 L14 17" stroke="#FDE68A" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}
